export type InvoiceNoteStatus = 'pending' | 'under_review' | 'approved' | 'rejected';

export type DocumentarySituation =
  | 'not_obligated'
  | 'not_submitted'
  | 'awaiting_review'
  | 'rejected'
  | 'approved';

export interface ObligationNote {
  id: string;
  invoiceNumber: string | null;
  status: InvoiceNoteStatus;
}

export interface StatusCounts {
  pending: number;
  under_review: number;
  approved: number;
  rejected: number;
}

export interface ObligationRow {
  userId: string;
  userName: string;
  contractType: string | null;
  competence: string;
  situation: DocumentarySituation;
  total: number;
  multiple: boolean;
  counts: StatusCounts;
  invoices: ObligationNote[];
  satisfiesDocumentarily: boolean;
  authorizesPayment: false;
}

export interface ObligationPage {
  items: ObligationRow[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
}

export const MONTHLY_PAGE_LIMIT = 10;

export const SITUATION_FILTER_ENABLED = false;

export const SITUATION_FILTER_PENDING =
  'O filtro por situação fica indisponível até a consulta mensal filtrar o conjunto inteiro, não só a página atual.';

export const NOT_OBLIGATED_MESSAGE =
  'Você não possui obrigação de envio de nota fiscal nesta competência.';

export const EMPTY_OBLIGATIONS_MESSAGE =
  'Nenhum colaborador com obrigação de nota fiscal nesta competência.';

export const INVOICE_MANAGEMENT_SECTION_ID = 'tc-hr-invoices';

export const RECEIVED_NOTES_PANEL_ID = 'invoice-received-panel';

export const MINE_HISTORY_SECTION_ID = 'tc-my-invoices-panel-trigger';

const CONTRACT_LABELS: Record<string, string> = {
  clt: 'CLT',
  mei: 'MEI',
  pj: 'PJ',
  other: 'Outro',
};

const SITUATION_LABELS: Record<DocumentarySituation, string> = {
  not_obligated: 'Sem obrigação',
  not_submitted: 'Não enviada',
  awaiting_review: 'Aguardando análise',
  rejected: 'Rejeitada',
  approved: 'Aprovada',
};

const NOTE_STATUS_LABELS: Record<InvoiceNoteStatus, string> = {
  pending: 'Pendente',
  under_review: 'Em análise',
  approved: 'Aprovada',
  rejected: 'Rejeitada',
};

export function currentCompetenceBrazil(now: Date = new Date()): string {
  const iso = now.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
  return iso.slice(0, 7);
}

export function contractTypeLabel(value?: string | null): string {
  if (!value) return 'Não informado';
  return CONTRACT_LABELS[value] || 'Não informado';
}

export function situationLabel(situation: DocumentarySituation): string {
  return SITUATION_LABELS[situation];
}

export function noteStatusLabel(status: InvoiceNoteStatus): string {
  return NOTE_STATUS_LABELS[status];
}

export function statusCountLabel(counts: StatusCounts): string {
  const parts = [
    counts.pending ? `${counts.pending} pendente${counts.pending === 1 ? '' : 's'}` : '',
    counts.under_review ? `${counts.under_review} em análise` : '',
    counts.approved ? `${counts.approved} aprovada${counts.approved === 1 ? '' : 's'}` : '',
    counts.rejected ? `${counts.rejected} rejeitada${counts.rejected === 1 ? '' : 's'}` : '',
  ].filter(Boolean);
  return parts.join(' · ');
}

export function documentaryCaption(row: Pick<ObligationRow, 'satisfiesDocumentarily'>): string | null {
  return row.satisfiesDocumentarily ? 'Obrigação documental atendida' : null;
}

export interface HistoryTarget {
  sectionId: string;
  userId: string | null;
  userName: string | null;
  competence: string;
  invoiceId: string | null;
  invoiceNumber: string | null;
}

export function reviewHistoryTarget(
  row: Pick<ObligationRow, 'userId' | 'userName' | 'competence'>,
  invoice?: { id: string; invoiceNumber?: string | null },
): HistoryTarget {
  return {
    sectionId: RECEIVED_NOTES_PANEL_ID,
    userId: row.userId,
    userName: row.userName,
    competence: row.competence,
    invoiceId: invoice?.id ?? null,
    invoiceNumber: invoice?.invoiceNumber?.trim() || null,
  };
}

export function mineHistoryTarget(competence: string, invoiceId?: string): HistoryTarget {
  return {
    sectionId: MINE_HISTORY_SECTION_ID,
    userId: null,
    userName: null,
    competence,
    invoiceId: invoiceId ?? null,
    invoiceNumber: null,
  };
}

export interface MonthlyRowView {
  userId: string;
  name: string;
  contract: string;
  situation: string;
  situationCode: DocumentarySituation;
  total: number;
  multipleLabel: string | null;
  countsLabel: string;
  documentary: string | null;
  notes: Array<{ id: string; number: string; status: string }>;
  history: HistoryTarget;
}

export function describeMonthlyRow(row: ObligationRow): MonthlyRowView {
  return {
    userId: row.userId,
    name: row.userName,
    contract: contractTypeLabel(row.contractType),
    situation: situationLabel(row.situation),
    situationCode: row.situation,
    total: row.total,
    multipleLabel: row.multiple ? 'Múltiplas notas' : null,
    countsLabel: statusCountLabel(row.counts),
    documentary: documentaryCaption(row),
    notes: row.invoices.map((invoice) => ({
      id: invoice.id,
      number: invoice.invoiceNumber?.trim() || 'Sem número',
      status: noteStatusLabel(invoice.status),
    })),
    history: reviewHistoryTarget(row),
  };
}

export function noteReviewTarget(
  row: Pick<ObligationRow, 'userId' | 'userName' | 'competence'>,
  note: { id: string; number: string },
): HistoryTarget {
  return reviewHistoryTarget(row, { id: note.id, invoiceNumber: note.number });
}

export function overviewMessage(page: Pick<ObligationPage, 'items' | 'total'>): string | null {
  if (page.total === 0) return EMPTY_OBLIGATIONS_MESSAGE;
  if (page.items.length === 0) return 'Nenhum colaborador nesta página.';
  return null;
}

export function applySituationFilter(page: ObligationPage): ObligationRow[] {
  return page.items;
}

export interface OwnSummary {
  competence: string;
  headline: string;
  situation: string | null;
  total: number | null;
  multipleLabel: string | null;
  countsLabel: string | null;
  documentary: string | null;
  notes: Array<{ id: string; number: string; status: string }>;
  history: HistoryTarget;
}

export function buildOwnSummary(row: ObligationRow, viewerId: string): OwnSummary | { blocked: true } {
  if (row.userId !== viewerId) {
    return { blocked: true };
  }
  if (row.situation === 'not_obligated') {
    return {
      competence: row.competence,
      headline: NOT_OBLIGATED_MESSAGE,
      situation: null,
      total: row.invoices.length,
      multipleLabel: row.multiple ? 'Múltiplas notas' : null,
      countsLabel: row.invoices.length ? statusCountLabel(row.counts) : null,
      documentary: null,
      notes: row.invoices.map((invoice) => ({
        id: invoice.id,
        number: invoice.invoiceNumber?.trim() || 'Sem número',
        status: noteStatusLabel(invoice.status),
      })),
      history: mineHistoryTarget(row.competence),
    };
  }
  const view = describeMonthlyRow(row);
  return {
    competence: row.competence,
    headline: view.situation,
    situation: view.situation,
    total: view.total,
    multipleLabel: view.multipleLabel,
    countsLabel: view.countsLabel,
    documentary: view.documentary,
    notes: view.notes,
    history: mineHistoryTarget(row.competence),
  };
}

export function monthlyInvoiceErrorMessage(error: { response?: { status?: number } } | null | undefined): string {
  const status = error?.response?.status;
  if (status === 401) return 'Sua sessão expirou. Entre novamente.';
  if (status === 403) return 'Você não tem permissão para ver esta consulta.';
  if (!error?.response) return 'Não foi possível conectar à API. Tente de novo em instantes.';
  return 'Não foi possível carregar o controle mensal.';
}
